// src/pages/offlineSales/components/FreeUnitsPopup.jsx
//
// Free-units picker popup, structurally cloned from components/ui/DiscountPercentagePopup.jsx
// (portal + backdrop + fixed positioning relative to the trigger button).
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Gift, Minus, Plus } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import itemStyles from '../../orders/components/OrderItemsSection.module.css';
import styles from './FreeUnitsPopup.module.css';

const MAX_PRESETS = 10;
const POPUP_WIDTH = 228;

const FreeUnitsPopup = ({ anchorEl, quantity, currentValue, onApply, onClose }) => {
  const { t } = useLocalization();
  const maxFree = Math.max(0, (parseInt(quantity, 10) || 0) - 1);
  const [customValue, setCustomValue] = useState(() => String(Math.min(Math.max(currentValue || 1, 1), Math.max(maxFree, 1))));
  const inputRef = useRef(null);
  const presets = Array.from({ length: Math.min(MAX_PRESETS, maxFree) }, (_, i) => i + 1);
  const customInt = parseInt(customValue, 10);
  const customValid = !isNaN(customInt) && customInt >= 1 && customInt <= maxFree;

  const style = useMemo(() => {
    if (!anchorEl) return { position: 'fixed', top: 0, left: 0, zIndex: 1400 };

    const rect = anchorEl.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    let left = rect.left;
    if (left + POPUP_WIDTH > viewportW - 8) {
      left = Math.max(8, viewportW - POPUP_WIDTH - 8);
    }

    const spaceBelow = viewportH - rect.bottom;
    if (spaceBelow < 260) {
      return { position: 'fixed', bottom: viewportH - rect.top + 4, left, zIndex: 1400 };
    }
    return { position: 'fixed', top: rect.bottom + 4, left, zIndex: 1400 };
  }, [anchorEl]);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  const apply = (value) => {
    onApply(value);
    onClose();
  };

  const handleApplyCustom = () => {
    if (customValid) apply(customInt);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleApplyCustom(); }
    if (e.key === 'Escape') onClose();
  };

  return ReactDOM.createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} />

      <div className={styles.popup} style={style} role="dialog" aria-label={t('offlineSales.freeUnits')}>
        <div className={styles.header}>
          <span className={styles.title}>
            <Gift size={12} />
            {t('offlineSales.freeUnits')}
          </span>
          <button type="button" className={styles.closeBtn} onClick={onClose} title={t('common.close')}>
            <X size={13} />
          </button>
        </div>

        {presets.length > 0 && (
          <div className={styles.presets}>
            {presets.map(n => (
              <button
                key={n}
                type="button"
                className={`${styles.presetBtn} ${currentValue === n ? styles.presetActive : ''}`}
                onClick={() => apply(n)}
              >
                {n}
              </button>
            ))}
          </div>
        )}

        <div className={styles.inputRow}>
          <div className={itemStyles.quantityWrapper}>
            <button
              type="button"
              onClick={() => setCustomValue(String(Math.max(1, (customInt || 1) - 1)))}
              className={itemStyles.quantityButton}
              disabled={!customInt || customInt <= 1}
            >
              <Minus size={14} />
            </button>
            <input
              ref={inputRef}
              type="number"
              min="1"
              max={maxFree}
              step="1"
              value={customValue}
              onChange={e => setCustomValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') e.preventDefault();
                handleKeyDown(e);
              }}
              placeholder={t('offlineSales.freeUnitsCustom')}
              title={t('offlineSales.freeUnitsHint')}
              className={itemStyles.quantityInput}
            />
            <button
              type="button"
              onClick={() => setCustomValue(String(Math.min(maxFree, (customInt || 0) + 1)))}
              className={itemStyles.quantityButton}
              disabled={customInt >= maxFree}
            >
              <Plus size={14} />
            </button>
          </div>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={handleApplyCustom}
            disabled={!customValid}
          >
            {t('offlineSales.freeUnitsApply')}
          </button>
        </div>

        {currentValue > 0 && (
          <button type="button" className={styles.clearBtn} onClick={() => apply(0)}>
            {t('common.clear')}
          </button>
        )}
      </div>
    </>,
    document.body
  );
};

export default FreeUnitsPopup;
